import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit, signal, ViewChild, inject } from "@angular/core";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { ChartConfigurationCustomTypesPerDataset, ChartDataset, ChartEvent, ChartType, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "payroll-turnover-chart",
    templateUrl: "./turnoverChart.tmpl.html",
    standalone: false
})
export class TurnoverChartComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $fileService = inject(FileService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();
    readonly customDataBinding: ChartBindingObject;
    readonly commands: ReadonlyArray<Command>;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfigurationCustomTypesPerDataset;

    private _chartCriteria: IChartCriteria;
    ChartTitle: string = "Employee Turnover";
    isLoaded: boolean;
    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this.customDataBinding?.chartId;
    }

    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    get ChartSubtitle(): string
    {
        if (!this.chartCriteria?.ChartDatas)
        {
            return null;
        }
        const fromDate = this.chartCriteria?.Parameters?.find(v => v.Id === "TurnoverFromDate")?.Value;
        const from = fromDate ? new Date(fromDate)?.toLocaleDateString() : "";

        const toDate = this.chartCriteria?.Parameters?.find(v => v.Id === "TurnoverToDate")?.Value;
        const to = toDate ? new Date(toDate)?.toLocaleDateString() : "";

        let sub = `${from} - ${to}`;
        sub = sub === " - " ? "" : sub;
        const department = this.getDepartmentText();
        if (department)
        {
            sub = `${sub} &bull; ${department}`;
        }
        return sub;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private getDepartmentText(): string
    {
        try
        {
            const departments = this.chartCriteria?.Parameters?.find(v => v.Id === "TurnoverDepartmentIds");
            const dept = departments.Value.split(",");
            const deptCodes = dept.map(g => departments.Options.filter(v => v.Value === g)[0].Display);
            const display = deptCodes.join(", ");
            return display;
        }
        catch
        {
            /* Squashing errors */
        }
        return null;
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService
            .GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const shortMonths = [...this.chartCriteria.ChartDatas[0].Labels];

        const hires = this.chartCriteria.ChartDatas[0];
        const terms = this.chartCriteria.ChartDatas[1];
        const turnovers = this.chartCriteria.ChartDatas[2];
        const rehires = this.chartCriteria.ChartDatas[3];

        const chartColors = this.$chartService.getColors(4);

        const hireColor = chartColors[0];
        const rehireColor = chartColors[1];
        const termColor = chartColors[3];
        const turnColor = this.$chartService.getTrendLineColor();

        this.chartConfig = {
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12 },
                        onClick: (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<ChartType>) =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = Utilities.IsNullOrUndefined(meta.hidden) ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (Utilities.IsNullOrUndefined(iMeta.hidden) && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<ChartType>[]) =>
                            {
                                return this.chartCriteria.ChartDatas[0].Labels[tooltipItems[0].dataIndex];
                            },
                            label: (tooltipItem: TooltipItem<ChartType>): string | string[] =>
                            {
                                return tooltipItem.label.toString();
                            },
                        },
                    },
                },
                elements: { line: { fill: false } },
                scales: {
                    x: {
                        display: true,
                        grid: { display: true },
                        labels: shortMonths,
                    },
                    barAxis: {
                        type: "linear",
                        display: true,
                        position: "right",
                        grid: { display: true },
                        title: {
                            display: true,
                            text: "Hire / Rehire / Terms",
                            font: { size: 16 },
                        },
                    },
                    lineAxis: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: false },
                        title: {
                            display: true,
                            text: "Growth",
                            font: { size: 16 },
                        },
                    },
                },
                datasets: {
                    bar: {
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,

                    },
                    line: {
                        tension: 0.5,
                        pointStyle: 'line',
                    },
                },
            },
            data: {
                datasets: [
                    {
                        type: "bar",
                        label: hires.SetLabel,
                        data: hires.Data,
                        yAxisID: "barAxis",
                        backgroundColor: hireColor,
                        borderColor: hireColor,
                        hoverBackgroundColor: hireColor,
                        hoverBorderColor: hireColor,
                    },
                    {
                        type: "bar",
                        label: rehires.SetLabel,
                        data: rehires.Data,
                        yAxisID: "barAxis",
                        backgroundColor: rehireColor,
                        borderColor: rehireColor,
                        hoverBackgroundColor: rehireColor,
                        hoverBorderColor: rehireColor,
                    },
                    {
                        type: "bar",
                        label: terms.SetLabel,
                        data: terms.Data,
                        yAxisID: "barAxis",
                        backgroundColor: termColor,
                        borderColor: termColor,
                        hoverBackgroundColor: termColor,
                        hoverBorderColor: termColor,
                    },
                    {
                        type: "line",
                        label: "Growth",
                        data: turnovers.Data,
                        yAxisID: "lineAxis",
                        fill: false,
                        backgroundColor: turnColor,
                        borderColor: turnColor,
                        pointHoverBackgroundColor: turnColor,
                        pointHoverBorderColor: turnColor,
                    },
                ],
            },
        };

        // TODO(DO-7754) - I don't know what this was trying to do...
        if (turnovers.Data.length > 1)
        {
            const lastDataset = this.chartConfig.data.datasets[this.chartConfig.data.datasets.length - 1] as ChartDataset<"line">;
            // ! TODO - yAxisID was set to this already...
            lastDataset.yAxisID = "lineAxis";
        }
        else
        {
            this.chartConfig.options.elements["point"] = {
                radius: 0,
                borderWidth: 0,
                hoverRadius: 0,
                hoverBorderWidth: 0,
            };
        }

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
